import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Layout} from '../../components/layout';
import {Toast, CellSelect} from '../../components';
import {
    carService,
    voyageService,
    portService,
    shipperService,
    consigneeService,
    modelSearchService,
} from '../../services';
import {useToast} from '../../hooks/useToast';
import {Voyage, Port, Shipper, Consignee, Make, Model} from '../../types';
import {handleDelete} from '../../utils/deleteUtils';

interface CarRowData {
    shipper_code: string;
    consignee_code: string;
    consignee_name: string;
    consignee_address: string;
    booking_reference: string;
    port_code: string;
    chassis_number: string;
    make_model: string;
    make_model_manual: string;
    year: string;
    freight: string;
    collect: string;
    remarks: string;
    category: string;
}

interface FailedCar {
    row: string;
    chassis: string;
    reason: string;
}

type ConflictDecision = 'pending' | 'keep_both' | 'override' | 'skip';

interface ConflictRecord {
    rowIndex: number;
    chassisNumber: string;
    decision: ConflictDecision;
    isPanelOpen: boolean;
    existingCar: {
        id: string;
        chassis: string;
        voyageCode: string | null;
        makeName: string | null;
        modelName: string | null;
        shipperName: string | null;
        consigneeName: string | null;
        year: number | null;
    };
}

const createEmptyRow = (): CarRowData => ({
    shipper_code: '',
    consignee_code: '',
    consignee_name: '',
    consignee_address: '',
    booking_reference: '',
    port_code: '',
    chassis_number: '',
    make_model: '',
    make_model_manual: '',
    year: '',
    freight: '',
    collect: '',
    remarks: '',
    category: '',
});

const COLUMN_KEYS: (keyof CarRowData)[] = [
    'shipper_code',
    'consignee_code',
    'consignee_name',
    'consignee_address',
    'booking_reference',
    'port_code',
    'chassis_number',
    'make_model',
    'year',
    'freight',
    'collect',
    'remarks',
    'category',
];

const COLUMN_HEADERS = [
    {key: 'shipper_code', label: 'SHIPPER*', width: '90px', type: 'dropdown'},
    {key: 'consignee_code', label: 'CONSIGNEE', width: '100px', type: 'dropdown'},
    {key: 'consignee_name', label: 'CONS. NAME*', width: '140px', type: 'text'},
    {key: 'consignee_address', label: 'CONS. ADDRESS', width: '160px', type: 'text'},
    {key: 'booking_reference', label: 'BOOKING #', width: '100px', type: 'text'},
    {key: 'port_code', label: 'DESTINATION*', width: '100px', type: 'dropdown'},
    {key: 'chassis_number', label: 'CHASSIS*', width: '140px', type: 'text'},
    {key: 'make_model', label: 'MAKE/MODEL*', width: '140px', type: 'dropdown'},
    {key: 'year', label: 'YEAR', width: '70px', type: 'text'},
    {key: 'freight', label: 'FREIGHT', width: '80px', type: 'text'},
    {key: 'collect', label: 'COLLECT', width: '80px', type: 'text'},
    {key: 'remarks', label: 'REMARKS', width: '150px', type: 'text'},
    {key: 'category', label: 'CAT', width: '60px', type: 'dropdown'},
];

// Columns that have CellSelect dropdowns
const DROPDOWN_COLUMNS = ['shipper_code', 'consignee_code', 'port_code', 'make_model', 'category'];

interface CellPosition {
    row: number;
    col: number;
}

interface SelectionRange {
    start: CellPosition;
    end: CellPosition;
}

const BulkAddCarsPage: React.FC = () => {
    const navigate = useNavigate();
    const {voyageId} = useParams<{ voyageId?: string }>();
    const {toast, showToast, hideToast} = useToast();
    const tableRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const shouldSelectOnFocus = useRef(false);
    const submitInFlight = useRef(false);

    const [voyage, setVoyage] = useState<Voyage | null>(null);
    const [rows, setRows] = useState<CarRowData[]>(() =>
        Array.from({length: 50}, () => createEmptyRow())
    );
    const [submitting, setSubmitting] = useState(false);
    const [failedCars, setFailedCars] = useState<FailedCar[]>([]);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [lastResult, setLastResult] = useState<{ created: number; updated: number; failed: number } | null>(null);
    const [internalDuplicateRows, setInternalDuplicateRows] = useState<Set<number>>(new Set());
    const [conflictMap, setConflictMap] = useState<Record<number, ConflictRecord>>({});
    const [isCheckingExisting, setIsCheckingExisting] = useState(false);

    // Selection and editing state
    const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
    const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
    const [editValue, setEditValue] = useState('');

    // Reference data
    const [ports, setPorts] = useState<Port[]>([]);
    const [shippers, setShippers] = useState<Shipper[]>([]);
    const [consignees, setConsignees] = useState<Consignee[]>([]);
    const [loading, setLoading] = useState(true);

    // Make/Model search state
    const [makeModelSearchResults, setMakeModelSearchResults] = useState<Array<{ model: Model; make: Make }>>([]);
    const [isMakeModelSearching, setIsMakeModelSearching] = useState(false);
    const makeModelAbortRef = useRef<AbortController | null>(null);
    const makeModelDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Map of "make_code - model_code" to "MAKE NAME - MODEL NAME" for display
    const [makeModelDisplayMap, setMakeModelDisplayMap] = useState<Record<string, string>>({});
    // Track which rows are in manual make/model entry mode (row index -> true)
    const [makeModelManualMode, setMakeModelManualMode] = useState<Record<number, boolean>>({});

    // Load reference data
    useEffect(() => {
        const loadData = async () => {
            try {
                const [portsData, shippersData, consigneesData] = await Promise.all([
                    portService.getAll({limit: 1000}),
                    shipperService.getAll({limit: 1000}),
                    consigneeService.getAll({limit: 1000}),
                ]);
                setPorts(portsData);
                setShippers(shippersData);
                setConsignees(consigneesData);

                if (voyageId) {
                    const voyageData = await voyageService.getById(voyageId);
                    setVoyage(voyageData);
                }
            } catch (error) {
                console.error('Error loading data:', error);
                showToast('Failed to load reference data', 'error');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [voyageId, showToast]);

    // Cleanup make/model search on unmount
    useEffect(() => {
        return () => {
            if (makeModelDebounceRef.current) clearTimeout(makeModelDebounceRef.current);
            if (makeModelAbortRef.current) makeModelAbortRef.current.abort();
        };
    }, []);

    // Focus management for editing
    useEffect(() => {
        if (editingCell) {
            const key = COLUMN_KEYS[editingCell.col];
            // Focus text input for non-dropdown columns, or for make_model in manual mode
            const isManualMakeModel = key === 'make_model' && makeModelManualMode[editingCell.row];
            if (!DROPDOWN_COLUMNS.includes(key) || isManualMakeModel) {
                inputRef.current?.focus({ preventScroll: true });
                // Only select text when editing via double-click/F2/Enter, not when typing a character
                if (shouldSelectOnFocus.current) {
                    inputRef.current?.select();
                }
            }
        }
        // Focus restoration to the table is handled in closeEditingCell inside the
        // double rAF callback, AFTER scroll position is restored. Doing it here
        // causes a race condition where the browser jumps before scroll restoration.
    }, [editingCell, makeModelManualMode]);

    // Transform make/model search results
    const transformMakeModelResults = useCallback((results: Awaited<ReturnType<typeof modelSearchService.searchWithMake>>) => {
        return results.map(result => ({
            model: {
                id: result.id,
                code: result.code,
                name: result.name,
                type: result.type,
                weight: result.weight,
                length: result.length,
                width: result.width,
                height: result.height,
                cbm: result.cbm,
                make_id: result.make_id,
            } as Model,
            make: {
                id: result.make.id,
                code: result.make.code,
                name: result.make.name,
                created_at: '',
                creator_id: '',
            } as Make
        }));
    }, []);

    // Load initial make/model data
    const loadInitialMakeModelData = useCallback(async () => {
        if (makeModelSearchResults.length > 0) return;

        setIsMakeModelSearching(true);
        makeModelAbortRef.current = new AbortController();

        try {
            const results = await modelSearchService.searchWithMake('', 50, makeModelAbortRef.current.signal);
            setMakeModelSearchResults(transformMakeModelResults(results));
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('Initial make/model load error:', error);
            }
        } finally {
            setIsMakeModelSearching(false);
        }
    }, [makeModelSearchResults.length, transformMakeModelResults]);

    // Search make/model with debounce
    const handleMakeModelSearch = useCallback(async (query: string) => {
        if (makeModelDebounceRef.current) clearTimeout(makeModelDebounceRef.current);
        if (makeModelAbortRef.current) makeModelAbortRef.current.abort();

        if (!query.trim()) {
            loadInitialMakeModelData();
            return;
        }

        makeModelDebounceRef.current = setTimeout(async () => {
            setIsMakeModelSearching(true);
            makeModelAbortRef.current = new AbortController();

            try {
                const results = await modelSearchService.searchWithMake(query.trim(), 50, makeModelAbortRef.current.signal);
                setMakeModelSearchResults(transformMakeModelResults(results));
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    console.error('Make/model search error:', error);
                    setMakeModelSearchResults([]);
                }
            } finally {
                setIsMakeModelSearching(false);
            }
        }, 300);
    }, [loadInitialMakeModelData, transformMakeModelResults]);

    // Get dropdown options for a column
    const getDropdownOptions = useCallback((key: string): { value: string; label: string }[] => {
        switch (key) {
            case 'shipper_code':
                return shippers.map(s => ({value: s.code, label: `${s.code} - ${s.shortname || s.name}`}));
            case 'consignee_code':
                return consignees.map(c => ({value: c.code, label: `${c.code} - ${c.name}`}));
            case 'port_code':
                return ports.map(p => ({value: p.code, label: `${p.code} - ${p.port}`}));
            case 'make_model':
                return makeModelSearchResults.map(r => ({
                    value: `${r.make.code} - ${r.model.code}`,
                    label: `${r.make.name} - ${r.model.name}`
                }));
            case 'category':
                return [
                    {value: 'L', label: 'L'},
                    {value: 'P', label: 'P'},
                    {value: 'G', label: 'G'},
                    {value: 'B', label: 'B'},
                ];
            default:
                return [];
        }
    }, [shippers, consignees, ports, makeModelSearchResults]);

    // Get display label for a dropdown value (converts code to readable label)
    const getDisplayLabel = useCallback((key: string, value: string): string => {
        if (!value) return '';
        if (key === 'make_model') {
            // Look up display name from our map, fallback to stored value
            return makeModelDisplayMap[value] || value;
        }
        const options = getDropdownOptions(key);
        const option = options.find(opt => opt.value === value);
        return option?.label || value;
    }, [getDropdownOptions, makeModelDisplayMap]);

    // Close editing cell while preserving scroll position
    const closeEditingCell = useCallback(() => {
        // Capture scroll position before state change causes re-render
        const scrollTop = tableRef.current?.scrollTop;
        const scrollLeft = tableRef.current?.scrollLeft;

        setEditingCell(null);

        // Restore scroll position after React reconciliation using double rAF
        // A single requestAnimationFrame runs before the browser paints the new frame.
        // If React takes time to destroy the DOM node and update the layout, scroll
        // restoration fires too early. Double rAF ensures the DOM is fully settled.
        if (scrollTop !== undefined && scrollLeft !== undefined) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (tableRef.current) {
                        // 1. Force the scroll position back
                        tableRef.current.scrollTop = scrollTop;
                        tableRef.current.scrollLeft = scrollLeft;

                        // 2. Restore focus AFTER scroll is corrected
                        // The browser sees the element is already in view at the correct
                        // position, so it won't trigger scroll-into-view behavior.
                        tableRef.current.focus({ preventScroll: true });
                    }
                });
            });
        }
    }, []);

    // Handle numeric input - filters non-numeric characters (allows decimal for freight/collect)
    const handleNumericInput = useCallback((value: string, allowDecimal: boolean = false): string => {
        if (allowDecimal) {
            // Allow digits and one decimal point
            return value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
        }
        // Only allow digits (for year)
        return value.replace(/\D/g, '');
    }, []);

    // Commit cell edit
    const commitEdit = useCallback((rowIndex: number, key: keyof CarRowData, value: string) => {
        setRows(prevRows => {
            const newRows = [...prevRows];
            const updatedRow = {...newRows[rowIndex]};

            // Uppercase chassis numbers
            if (key === 'chassis_number') {
                value = value.toUpperCase();
            }

            // Apply numeric filtering for numeric fields
            if (key === 'year') {
                value = handleNumericInput(value, false);
            } else if (key === 'freight' || key === 'collect') {
                value = handleNumericInput(value, true);
            }

            updatedRow[key] = value;

            // Auto-fill from consignee
            if (key === 'consignee_code' && value) {
                const consignee = consignees.find(c => c.code.toLowerCase() === value.toLowerCase());
                if (consignee) {
                    updatedRow.consignee_name = consignee.name || '';
                    const addressParts = [consignee.address, consignee.street, consignee.city].filter(Boolean);
                    updatedRow.consignee_address = addressParts.join(', ');

                    if (!updatedRow.shipper_code && consignee.shipper_id) {
                        const shipper = shippers.find(s => s.id === consignee.shipper_id);
                        if (shipper) updatedRow.shipper_code = shipper.code;
                    }

                    if (!updatedRow.port_code && consignee.port_id) {
                        const port = ports.find(p => p.id === consignee.port_id);
                        if (port) updatedRow.port_code = port.code;
                    }
                }
            }

            newRows[rowIndex] = updatedRow;
            return newRows;
        });
    }, [consignees, shippers, ports, handleNumericInput]);

    // Get normalized selection bounds
    const getSelectionBounds = useCallback(() => {
        if (!selectionRange) return null;
        const minRow = Math.min(selectionRange.start.row, selectionRange.end.row);
        const maxRow = Math.max(selectionRange.start.row, selectionRange.end.row);
        const minCol = Math.min(selectionRange.start.col, selectionRange.end.col);
        const maxCol = Math.max(selectionRange.start.col, selectionRange.end.col);
        return {minRow, maxRow, minCol, maxCol};
    }, [selectionRange]);

    // Check if cell is in selection
    const isCellInSelection = useCallback((row: number, col: number) => {
        const bounds = getSelectionBounds();
        if (!bounds) return false;
        return row >= bounds.minRow && row <= bounds.maxRow && col >= bounds.minCol && col <= bounds.maxCol;
    }, [getSelectionBounds]);

    // Handle mouse down on cell (start selection)
    const handleCellMouseDown = useCallback((row: number, col: number, e: React.MouseEvent) => {
        // If clicking on a different cell, commit current edit
        if (editingCell && (editingCell.row !== row || editingCell.col !== col)) {
            const editKey = COLUMN_KEYS[editingCell.col];
            // For make_model in manual mode, commit to make_model_manual field
            const commitKey = (editKey === 'make_model' && makeModelManualMode[editingCell.row])
                ? 'make_model_manual'
                : editKey;
            commitEdit(editingCell.row, commitKey, editValue);
            closeEditingCell();
        }

        if (e.shiftKey && selectedCell) {
            // Shift+click extends selection
            setSelectionRange({
                start: selectedCell,
                end: {row, col}
            });
        } else {
            // Normal click starts new selection
            setSelectedCell({row, col});
            setSelectionRange({
                start: {row, col},
                end: {row, col}
            });
            setIsDragging(true);
        }
    }, [editingCell, editValue, commitEdit, selectedCell, closeEditingCell, makeModelManualMode]);

    // Handle mouse enter on cell (extend selection while dragging)
    const handleCellMouseEnter = useCallback((row: number, col: number) => {
        if (isDragging && selectionRange) {
            setSelectionRange({
                ...selectionRange,
                end: {row, col}
            });
        }
    }, [isDragging, selectionRange]);

    // Handle mouse up (end selection)
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add mouse up listener
    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseUp]);

    // Handle cell double-click (edit)
    const handleCellDoubleClick = useCallback((row: number, col: number) => {
        const key = COLUMN_KEYS[col];
        const currentValue = rows[row][key];
        // For make_model in manual mode, use the manual value
        if (key === 'make_model' && makeModelManualMode[row]) {
            setEditValue(rows[row].make_model_manual);
        } else if (DROPDOWN_COLUMNS.includes(key) || key === 'make_model') {
            // For dropdowns, don't pre-fill search input on double-click
            setEditValue('');
        } else {
            setEditValue(currentValue);
        }
        // Select text when editing starts via double-click/F2/Enter
        shouldSelectOnFocus.current = true;
        setEditingCell({row, col});
        setSelectedCell({row, col});
        setSelectionRange({start: {row, col}, end: {row, col}});
    }, [rows, makeModelManualMode]);

    // Start editing with initial character
    const startEditingWithChar = useCallback((row: number, col: number, char: string) => {
        // For all columns (including dropdowns), start with the typed character
        // Don't select text when starting to type - user wants to enter new content
        shouldSelectOnFocus.current = false;
        setEditValue(char);
        setEditingCell({row, col});
    }, []);

    // Delete selected rows (for Ctrl+Delete)
    const deleteSelectedRows = useCallback(async () => {
        const bounds = getSelectionBounds();
        if (!bounds) return;

        const rowCount = bounds.maxRow - bounds.minRow + 1;
        const message = rowCount === 1
            ? `Delete row ${bounds.minRow + 1}?`
            : `Delete ${rowCount} rows (${bounds.minRow + 1} to ${bounds.maxRow + 1})?`;

        await handleDelete(async () => {
            setRows(prev => {
                const newRows = prev.filter((_, i) => i < bounds.minRow || i > bounds.maxRow);
                // Ensure at least 1 row remains
                return newRows.length > 0 ? newRows : [createEmptyRow()];
            });

            // Update makeModelManualMode - remove deleted indices and shift remaining
            setMakeModelManualMode(prev => {
                const newMode: Record<number, boolean> = {};
                const deletedCount = bounds.maxRow - bounds.minRow + 1;
                Object.keys(prev).forEach(key => {
                    const idx = parseInt(key, 10);
                    if (idx < bounds.minRow) {
                        newMode[idx] = prev[idx];
                    } else if (idx > bounds.maxRow) {
                        newMode[idx - deletedCount] = prev[idx];
                    }
                    // indices in deleted range are removed
                });
                return newMode;
            });

            // Clear selection and editing state after delete
            setSelectedCell(null);
            setSelectionRange(null);
            setEditingCell(null);
            // Clear conflict map since row indices are now stale
            setConflictMap({});
        }, message);
    }, [getSelectionBounds]);

    // Delete single row with confirmation
    const deleteRow = useCallback(async (rowIndex: number) => {
        const row = rows[rowIndex];
        const identifier = row.chassis_number || `Row ${rowIndex + 1}`;

        await handleDelete(async () => {
            setRows(prev => {
                const newRows = prev.filter((_, i) => i !== rowIndex);
                // Ensure at least 1 row remains
                return newRows.length > 0 ? newRows : [createEmptyRow()];
            });

            // Update makeModelManualMode - shift indices for rows after deleted row
            setMakeModelManualMode(prev => {
                const newMode: Record<number, boolean> = {};
                Object.keys(prev).forEach(key => {
                    const idx = parseInt(key, 10);
                    if (idx < rowIndex) {
                        newMode[idx] = prev[idx];
                    } else if (idx > rowIndex) {
                        newMode[idx - 1] = prev[idx];
                    }
                    // idx === rowIndex is removed
                });
                return newMode;
            });

            // Clear editing state if editing the deleted row
            if (editingCell && editingCell.row === rowIndex) {
                setEditingCell(null);
            } else if (editingCell && editingCell.row > rowIndex) {
                setEditingCell(prev => prev ? {...prev, row: prev.row - 1} : null);
            }

            // Clear selection if deleted row was selected
            if (selectedCell && selectedCell.row === rowIndex) {
                setSelectedCell(null);
                setSelectionRange(null);
            } else if (selectedCell && selectedCell.row > rowIndex) {
                // Adjust selection index if it was after deleted row
                setSelectedCell(prev => prev ? {...prev, row: prev.row - 1} : null);
                setSelectionRange(prev => prev ? {
                    start: {...prev.start, row: prev.start.row > rowIndex ? prev.start.row - 1 : prev.start.row},
                    end: {...prev.end, row: prev.end.row > rowIndex ? prev.end.row - 1 : prev.end.row}
                } : null);
            }
            // Clear conflict map since row indices are now stale
            setConflictMap({});
        }, `Delete "${identifier}"?`);
    }, [rows, selectedCell, editingCell]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!selectedCell) return;

        const {row, col} = selectedCell;
        const maxCol = COLUMN_KEYS.length - 1;
        const maxRow = rows.length - 1;

        // If we're editing
        if (editingCell) {
            const editKey = COLUMN_KEYS[editingCell.col];
            // Check if currently editing a dropdown (react-select handles its own Enter key)
            const isDropdownEditing = DROPDOWN_COLUMNS.includes(editKey) && 
                (editKey !== 'make_model' || !makeModelManualMode[editingCell.row]);

            if (e.key === 'Escape') {
                e.preventDefault();
                closeEditingCell();
                setEditValue('');
            } else if (e.key === 'Enter') {
                // Let react-select handle Enter for dropdowns
                if (isDropdownEditing) {
                    return;
                }
                e.preventDefault();
                // For make_model in manual mode, commit to make_model_manual field
                const commitKey = (editKey === 'make_model' && makeModelManualMode[editingCell.row])
                    ? 'make_model_manual'
                    : COLUMN_KEYS[editingCell.col];
                commitEdit(editingCell.row, commitKey, editValue);
                closeEditingCell();
                // Move to next row
                if (row < maxRow) {
                    setSelectedCell({row: row + 1, col});
                    setSelectionRange({start: {row: row + 1, col}, end: {row: row + 1, col}});
                }
            } else if (e.key === 'Tab') {
                e.preventDefault();
                // For make_model in manual mode, commit to make_model_manual field
                const commitKey = (editKey === 'make_model' && makeModelManualMode[editingCell.row])
                    ? 'make_model_manual'
                    : COLUMN_KEYS[editingCell.col];
                commitEdit(editingCell.row, commitKey, editValue);
                closeEditingCell();
                // Move to next/prev cell
                const nextCol = e.shiftKey ? col - 1 : col + 1;
                if (nextCol > maxCol && row < maxRow) {
                    setSelectedCell({row: row + 1, col: 0});
                    setSelectionRange({start: {row: row + 1, col: 0}, end: {row: row + 1, col: 0}});
                } else if (nextCol < 0 && row > 0) {
                    setSelectedCell({row: row - 1, col: maxCol});
                    setSelectionRange({start: {row: row - 1, col: maxCol}, end: {row: row - 1, col: maxCol}});
                } else if (nextCol >= 0 && nextCol <= maxCol) {
                    setSelectedCell({row, col: nextCol});
                    setSelectionRange({start: {row, col: nextCol}, end: {row, col: nextCol}});
                }
            }
            return;
        }

        // Navigation when not editing
        // For Shift+Arrow, use selectionRange.end as the current position to extend from
        const currentRow = e.shiftKey && selectionRange ? selectionRange.end.row : row;
        const currentCol = e.shiftKey && selectionRange ? selectionRange.end.col : col;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (currentRow < maxRow) {
                const newPos = {row: currentRow + 1, col: currentCol};
                if (e.shiftKey && selectionRange) {
                    setSelectionRange({...selectionRange, end: newPos});
                } else {
                    setSelectedCell(newPos);
                    setSelectionRange({start: newPos, end: newPos});
                }
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (currentRow > 0) {
                const newPos = {row: currentRow - 1, col: currentCol};
                if (e.shiftKey && selectionRange) {
                    setSelectionRange({...selectionRange, end: newPos});
                } else {
                    setSelectedCell(newPos);
                    setSelectionRange({start: newPos, end: newPos});
                }
            }
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            if (currentCol < maxCol) {
                const newPos = {row: currentRow, col: currentCol + 1};
                if (e.shiftKey && selectionRange) {
                    setSelectionRange({...selectionRange, end: newPos});
                } else {
                    setSelectedCell(newPos);
                    setSelectionRange({start: newPos, end: newPos});
                }
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (currentCol > 0) {
                const newPos = {row: currentRow, col: currentCol - 1};
                if (e.shiftKey && selectionRange) {
                    setSelectionRange({...selectionRange, end: newPos});
                } else {
                    setSelectedCell(newPos);
                    setSelectionRange({start: newPos, end: newPos});
                }
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            const nextCol = e.shiftKey ? col - 1 : col + 1;
            if (nextCol > maxCol && row < maxRow) {
                const newPos = {row: row + 1, col: 0};
                setSelectedCell(newPos);
                setSelectionRange({start: newPos, end: newPos});
            } else if (nextCol < 0 && row > 0) {
                const newPos = {row: row - 1, col: maxCol};
                setSelectedCell(newPos);
                setSelectionRange({start: newPos, end: newPos});
            } else if (nextCol >= 0 && nextCol <= maxCol) {
                const newPos = {row, col: nextCol};
                setSelectedCell(newPos);
                setSelectionRange({start: newPos, end: newPos});
            }
        } else if (e.key === 'Enter' || e.key === 'F2') {
            e.preventDefault();
            handleCellDoubleClick(row, col);
        } else if (e.key === 'Delete' && (e.ctrlKey || e.metaKey)) {
            // Ctrl+Delete or Cmd+Delete: Delete selected rows
            e.preventDefault();
            deleteSelectedRows();
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            // Delete all cells in selection
            const bounds = getSelectionBounds();
            if (bounds) {
                setRows(prevRows => {
                    const newRows = [...prevRows];
                    for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
                        const updatedRow = {...newRows[r]};
                        for (let c = bounds.minCol; c <= bounds.maxCol; c++) {
                            const key = COLUMN_KEYS[c];
                            updatedRow[key] = '';
                            // Also clear manual value if this row is in manual make/model mode
                            if (key === 'make_model' && makeModelManualMode[r]) {
                                updatedRow['make_model_manual'] = '';
                            }
                        }
                        newRows[r] = updatedRow;
                    }
                    return newRows;
                });
            }
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
            // Start editing with the typed character
            e.preventDefault();
            startEditingWithChar(row, col, e.key);
        }
    }, [selectedCell, editingCell, editValue, rows.length, selectionRange, commitEdit, handleCellDoubleClick, startEditingWithChar, getSelectionBounds, closeEditingCell, makeModelManualMode, deleteSelectedRows]);

    // Handle paste from Excel
    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (!selectedCell) return;

        // If we're actively editing a text input cell, let native paste work.
        // The controlled input's onChange handler will update editValue.
        if (editingCell) {
            const editKey = COLUMN_KEYS[editingCell.col];
            const isManualMakeModel = editKey === 'make_model' && makeModelManualMode[editingCell.row];
            const isTextInput = !DROPDOWN_COLUMNS.includes(editKey) || isManualMakeModel;
            if (isTextInput) {
                return;
            }
        }

        const clipboardData = e.clipboardData.getData('text');
        if (!clipboardData) return;

        e.preventDefault();

        const {row: startRow, col: startCol} = selectedCell;

        // Check if it's multi-cell data (contains tabs or newlines)
        if (clipboardData.includes('\t') || clipboardData.includes('\n')) {
            // Split by newlines and remove only trailing empty rows (preserve empty rows in the middle)
            const allRows = clipboardData.split('\n');
            // Find the last non-empty row index
            let lastNonEmptyIndex = allRows.length - 1;
            while (lastNonEmptyIndex >= 0 && !allRows[lastNonEmptyIndex].trim()) {
                lastNonEmptyIndex--;
            }
            // Keep all rows up to and including the last non-empty row
            const pasteRows = allRows.slice(0, lastNonEmptyIndex + 1);

            setRows(prevRows => {
                const newRows = [...prevRows];

                // Ensure we have enough rows
                const neededRows = startRow + pasteRows.length;
                while (newRows.length < neededRows) {
                    newRows.push(createEmptyRow());
                }

                pasteRows.forEach((pasteRow, rowOffset) => {
                    const cells = pasteRow.split('\t');
                    const targetRowIndex = startRow + rowOffset;
                    const updatedRow = {...newRows[targetRowIndex]};

                    cells.forEach((cellValue, colOffset) => {
                        const targetCol = startCol + colOffset;
                        if (targetCol < COLUMN_KEYS.length) {
                            const key = COLUMN_KEYS[targetCol];
                            // When pasting into the make_model column for a row in manual mode,
                            // write to make_model_manual so the value is visible
                            const effectiveKey: keyof CarRowData = (key === 'make_model' && makeModelManualMode[targetRowIndex])
                                ? 'make_model_manual'
                                : key;
                            let value = cellValue.trim();

                            if (effectiveKey === 'chassis_number') {
                                value = value.toUpperCase();
                            }

                            updatedRow[effectiveKey] = value;
                        }
                    });

                    newRows[targetRowIndex] = updatedRow;
                });

                return newRows;
            });

            showToast(`Pasted ${pasteRows.length} row(s)`, 'success');
        } else {
            // Single cell paste — respect manual make/model mode
            const pasteKey = COLUMN_KEYS[startCol];
            const effectiveKey: keyof CarRowData = (pasteKey === 'make_model' && makeModelManualMode[startRow])
                ? 'make_model_manual'
                : pasteKey;
            commitEdit(startRow, effectiveKey, clipboardData.trim());
        }
    }, [selectedCell, editingCell, makeModelManualMode, commitEdit, showToast]);

    // Handle copy
    const handleCopy = useCallback((e: React.ClipboardEvent) => {
        const bounds = getSelectionBounds();
        if (!bounds) return;

        e.preventDefault();

        const copyData: string[] = [];
        for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
            const rowData: string[] = [];
            for (let c = bounds.minCol; c <= bounds.maxCol; c++) {
                const key = COLUMN_KEYS[c];
                // For make_model in manual mode, copy the manual value (not the empty dropdown field)
                const effectiveKey: keyof CarRowData = (key === 'make_model' && makeModelManualMode[r])
                    ? 'make_model_manual'
                    : key;
                rowData.push(rows[r][effectiveKey]);
            }
            copyData.push(rowData.join('\t'));
        }

        e.clipboardData.setData('text/plain', copyData.join('\n'));
        showToast('Copied to clipboard', 'success');
    }, [getSelectionBounds, rows, makeModelManualMode, showToast]);

    // Add rows
    const addRows = useCallback((count: number) => {
        const newRows = Array.from({length: count}, () => createEmptyRow());
        setRows(prev => [...prev, ...newRows]);
    }, []);

    // Clear all
    const clearAll = useCallback(() => {
        setRows(Array.from({length: 50}, () => createEmptyRow()));
        setFailedCars([]);
        setLastResult(null);
        setSelectedCell(null);
        setSelectionRange(null);
        setEditingCell(null);
        setMakeModelDisplayMap({});
        setMakeModelManualMode({});
        setConflictMap({});
        setInternalDuplicateRows(new Set());
    }, []);

    // Validate rows
    const getValidRows = useCallback((): CarRowData[] => {
        return rows.filter(row => {
            const hasData = row.shipper_code || row.consignee_code || row.consignee_name ||
                row.port_code || row.chassis_number || row.make_model || row.make_model_manual;
            if (!hasData) return false;

            return row.shipper_code &&
                row.consignee_name &&
                row.port_code &&
                row.chassis_number &&
                (row.make_model || row.make_model_manual);
        });
    }, [rows]);

    const validRowCount = useMemo(() => getValidRows().length, [getValidRows]);

    // Get selection count
    const selectionCount = useMemo(() => {
        const bounds = getSelectionBounds();
        if (!bounds) return 0;
        return (bounds.maxRow - bounds.minRow + 1) * (bounds.maxCol - bounds.minCol + 1);
    }, [getSelectionBounds]);

    // Helper to build the car payload from a row
    const buildCarPayload = useCallback((row: CarRowData) => {
        const makeModel = row.make_model;
        const manualMakeModel = row.make_model_manual;
        let makeCode = '';
        let modelCode = '';

        if (makeModel && makeModel.includes(' - ')) {
            const parts = makeModel.split(' - ');
            makeCode = parts[0] || '';
            modelCode = parts[1] || '';
        }

        return {
            chassis_number: row.chassis_number.trim(),
            name: row.consignee_name || undefined,
            address: row.consignee_address || undefined,
            booking_reference: row.booking_reference || undefined,
            year: row.year ? parseInt(row.year, 10) : undefined,
            freight: row.freight ? parseFloat(row.freight) : 0,
            collect: row.collect ? parseFloat(row.collect) : undefined,
            prepaid_or_collect: row.collect ? 'C' as const : 'P' as const,
            remarks: row.remarks || undefined,
            port_code: row.port_code || undefined,
            shipper_code: row.shipper_code || undefined,
            consignee_code: row.consignee_code || undefined,
            make_code: makeCode || undefined,
            model_code: modelCode || undefined,
            manual_make_model: manualMakeModel || undefined,
            category: row.category || undefined,
        };
    }, []);

    // Submit
    const handleSubmit = async () => {
        if (submitInFlight.current) return;
        submitInFlight.current = true;
        try {

        // Commit any pending edit
        if (editingCell) {
            const editKey = COLUMN_KEYS[editingCell.col];
            const commitKey = (editKey === 'make_model' && makeModelManualMode[editingCell.row])
                ? 'make_model_manual'
                : editKey;
            commitEdit(editingCell.row, commitKey, editValue);
            setEditingCell(null);
        }

        if (!voyage) {
            showToast('No voyage selected', 'error');
            return;
        }

        const validRows = getValidRows();

        if (validRows.length === 0) {
            showToast('No valid rows to add. Please fill in required fields (marked with *)', 'error');
            return;
        }

        // Phase 1: Internal duplicate check (client-side, before any API call)
        const chassisSeen = new Map<string, number>(); // chassis -> first rowIndex
        const duplicateIndices = new Set<number>();
        validRows.forEach((row) => {
            const actualIdx = rows.findIndex(r => r === row);
            const chassis = row.chassis_number.toUpperCase();
            if (chassisSeen.has(chassis)) {
                duplicateIndices.add(chassisSeen.get(chassis)!);
                duplicateIndices.add(actualIdx);
            } else {
                chassisSeen.set(chassis, actualIdx);
            }
        });

        if (duplicateIndices.size > 0) {
            setInternalDuplicateRows(duplicateIndices);
            const dupCount = Array.from(chassisSeen.entries()).filter(([, idx]) => duplicateIndices.has(idx)).length;
            showToast(`Cannot submit: ${dupCount} duplicate chassis number(s) found in your batch`, 'error');
            setSubmitting(false);
            return;
        }
        setInternalDuplicateRows(new Set());

        // Phase 2: If conflictMap is empty, do the pre-flight check against the system
        if (Object.keys(conflictMap).length === 0) {
            setIsCheckingExisting(true);
            try {
                const allChassis = validRows.map(r => r.chassis_number.trim());
                const checkResult = await carService.bulkCheckExisting(allChassis);

                if (checkResult.existing.length > 0) {
                    const newConflictMap: Record<number, ConflictRecord> = {};
                    checkResult.existing.forEach(existing => {
                        const rowIdx = rows.findIndex(r => r.chassis_number.toUpperCase() === existing.chassis_number.toUpperCase());
                        if (rowIdx !== -1) {
                            newConflictMap[rowIdx] = {
                                rowIndex: rowIdx,
                                chassisNumber: existing.chassis_number,
                                decision: 'pending',
                                isPanelOpen: true,
                                existingCar: {
                                    id: existing.id,
                                    chassis: existing.chassis_number,
                                    voyageCode: existing.voyage_code,
                                    makeName: existing.make_name,
                                    modelName: existing.model_name,
                                    shipperName: existing.shipper_name,
                                    consigneeName: existing.consignee_name,
                                    year: existing.year,
                                }
                            };
                        }
                    });
                    setConflictMap(newConflictMap);
                    showToast(`${checkResult.existing.length} chassis already exist in the system. Please resolve conflicts.`, 'warning');
                    return;
                }
            } finally {
                setIsCheckingExisting(false);
            }
        }

        // Phase 3: Submit (conflictMap is either empty or fully resolved)
        const pendingCount = Object.values(conflictMap).filter(c => c.decision === 'pending').length;
        if (pendingCount > 0) {
            showToast(`Please resolve all ${pendingCount} conflicts before submitting`, 'warning');
            return;
        }

        // Build final list: skip rows with 'skip' decision, collect overrides and keep_both
        const skipChassisSet = new Set(
            Object.values(conflictMap).filter(c => c.decision === 'skip').map(c => c.chassisNumber.toUpperCase())
        );
        const overrideChassis = Object.values(conflictMap)
            .filter(c => c.decision === 'override')
            .map(c => c.chassisNumber);
        const keepBothChassis = Object.values(conflictMap)
            .filter(c => c.decision === 'keep_both')
            .map(c => c.chassisNumber);

        const rowsToSubmit = validRows.filter(r => !skipChassisSet.has(r.chassis_number.toUpperCase()));

        if (rowsToSubmit.length === 0) {
            showToast('All rows were skipped. Nothing to submit.', 'warning');
            return;
        }

        setSubmitting(true);
        setFailedCars([]);
        setLastResult(null);

        try {
            const carsToCreate = rowsToSubmit.map(row => buildCarPayload(row));

            const result = await carService.bulkCreateCars(voyage.id, carsToCreate, overrideChassis, keepBothChassis);

            const failed = result.failed || [];
            setLastResult({created: result.created_count, updated: result.updated_count ?? 0, failed: failed.length});

            if (failed.length > 0) {
                setFailedCars(failed);
                setShowFailedModal(true);
                const processedMsg = result.updated_count ? `Created ${result.created_count}, overrode ${result.updated_count}` : `Created ${result.created_count}`;
                showToast(`${processedMsg}. ${failed.length} failed.`, 'warning');
            } else {
                showToast(result.message, 'success');
                if (result.created_count > 0 || (result.updated_count ?? 0) > 0) {
                    clearAll();
                }
            }
        } catch (error: any) {
            console.error('Error creating cars:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to create cars';
            showToast(errorMessage, 'error');
        } finally {
            setSubmitting(false);
        }

        } finally {
            submitInFlight.current = false;
        }
    };

    // Check if cell is the primary selected cell
    const isCellSelected = useCallback((row: number, col: number) => {
        return selectedCell?.row === row && selectedCell?.col === col;
    }, [selectedCell]);

    // Check if cell is being edited
    const isCellEditing = useCallback((row: number, col: number) => {
        return editingCell?.row === row && editingCell?.col === col;
    }, [editingCell]);

    if (loading) {
        return (
            <Layout fullWidth>
                <div className="flex items-center justify-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout fullWidth>
            <div className="h-[calc(100vh-120px)] flex flex-col px-4 py-4">
                {/* Header */}
                <div className="mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors mb-2 flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                        </svg>
                        Back
                    </button>
                    <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                        </svg>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            Bulk Add Cars
                        </h1>
                        {voyage && (
                            <span className="text-lg text-gray-500 dark:text-gray-400">
                                - {voyage.voyage_code}
                            </span>
                        )}
                        <span className="ml-2 text-sm text-gray-500">
                            ({validRowCount} valid rows)
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Click to select, drag to multi-select, double-click or type to edit, Ctrl+V to paste, Ctrl+Delete to delete rows.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mb-3">
                    <button
                        onClick={() => addRows(50)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                        </svg>
                        Add 50 Rows
                    </button>
                    <button
                        onClick={clearAll}
                        className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                        Clear All
                    </button>
                    <div className="flex-1"/>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    {(() => {
                        const pendingCount = Object.values(conflictMap).filter(c => c.decision === 'pending').length;
                        const skipCount = Object.values(conflictMap).filter(c => c.decision === 'skip').length;
                        const finalCount = validRowCount - skipCount;
                        const isDisabled = submitting || isCheckingExisting || !voyage || validRowCount === 0 || pendingCount > 0;
                        let label: string;
                        if (isCheckingExisting) {
                            label = 'Checking...';
                        } else if (submitting) {
                            label = 'Adding...';
                        } else if (pendingCount > 0) {
                            label = `Resolve ${pendingCount} Conflict${pendingCount !== 1 ? 's' : ''} to Continue`;
                        } else if (skipCount > 0 && finalCount !== validRowCount) {
                            label = `Add ${finalCount} Car${finalCount !== 1 ? 's' : ''} (${skipCount} skipped)`;
                        } else {
                            label = `Add ${validRowCount} Car${validRowCount !== 1 ? 's' : ''}`;
                        }
                        return (
                            <button
                                onClick={handleSubmit}
                                disabled={isDisabled}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                                {label}
                            </button>
                        );
                    })()}
                </div>

                {/* Internal duplicate error banner */}
                {internalDuplicateRows.size > 0 && (
                    <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-900/30 border border-red-700/60 rounded-lg text-red-400 text-sm">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        Cannot submit: duplicate chassis numbers found in batch (
                        {new Set(Array.from(internalDuplicateRows).map(idx => rows[idx]?.chassis_number.toUpperCase()).filter(Boolean)).size} chassis,&nbsp;
                        {internalDuplicateRows.size} rows highlighted). Resolve all duplicates to continue.
                    </div>
                )}

                {/* Spreadsheet Table */}
                <div
                    ref={tableRef}
                    className="flex-1 overflow-auto border border-gray-700 rounded-lg bg-gray-900 focus:outline-none select-none"
                    style={{ overflowAnchor: 'none' }}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onCopy={handleCopy}
                >
                    <table className="w-full border-collapse" style={{minWidth: '1490px'}}>
                        <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-800">
                            <th className="px-2 py-2 text-xs font-semibold text-gray-400 border-b border-r border-gray-700 bg-gray-800 w-12">
                                #
                            </th>
                            {COLUMN_HEADERS.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-2 py-2 text-xs font-semibold text-gray-400 border-b border-r border-gray-700 bg-gray-800 text-left"
                                    style={{width: col.width, minWidth: col.width}}
                                >
                                    {col.label}
                                </th>
                            ))}
                            <th className="px-1 py-2 text-xs font-semibold text-gray-400 border-b border-gray-700 bg-gray-800 w-8">
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, rowIndex) => {
                            const hasData = Object.values(row).some(v => v);
                            const isValid = row.shipper_code &&
                                row.consignee_name &&
                                row.port_code &&
                                row.chassis_number &&
                                (row.make_model || row.make_model_manual);

                            const conflict = conflictMap[rowIndex];
                            const isInternalDuplicate = internalDuplicateRows.has(rowIndex);

                            // Determine left-border class for conflict state
                            let conflictBorderClass = '';
                            if (conflict) {
                                if (conflict.decision === 'pending') conflictBorderClass = 'border-l-2 border-l-amber-500';
                                else if (conflict.decision === 'keep_both') conflictBorderClass = 'border-l-2 border-l-blue-500';
                                else if (conflict.decision === 'override') conflictBorderClass = 'border-l-2 border-l-green-500';
                                else if (conflict.decision === 'skip') conflictBorderClass = 'border-l-2 border-l-gray-500';
                            } else if (isInternalDuplicate) {
                                conflictBorderClass = 'border-l-2 border-l-red-500';
                            }

                            return (
                                <React.Fragment key={rowIndex}>
                                <tr
                                    className={`
                                            ${hasData && !isValid ? 'bg-red-900/10' : ''}
                                            ${hasData && isValid ? 'bg-green-900/10' : ''}
                                            ${conflictBorderClass}
                                        `}
                                >
                                    <td className="px-2 py-1.5 text-xs text-gray-500 border-b border-r border-gray-700 text-center bg-gray-850">
                                        {rowIndex + 1}
                                    </td>
                                    {COLUMN_KEYS.map((key, colIndex) => {
                                        const isSelected = isCellSelected(rowIndex, colIndex);
                                        const isInSelection = isCellInSelection(rowIndex, colIndex);
                                        const isEditing = isCellEditing(rowIndex, colIndex);
                                        const isDropdown = DROPDOWN_COLUMNS.includes(key);
                                        const colHeader = COLUMN_HEADERS[colIndex];

                                        return (
                                            <td
                                                key={key}
                                                className={`
                                                        p-0 border-b border-r border-gray-700 relative cursor-cell
                                                        ${isSelected ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}
                                                        ${isInSelection && !isSelected ? 'bg-blue-900/30' : ''}
                                                    `}
                                                onMouseDown={(e) => handleCellMouseDown(rowIndex, colIndex, e)}
                                                onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                                                onDoubleClick={() => handleCellDoubleClick(rowIndex, colIndex)}
                                            >
                                                {/* Special handling for make_model column with manual toggle */}
                                                {key === 'make_model' ? (
                                                    <div className="flex items-center">
                                                        <div className="flex-1">
                                                            {isEditing ? (
                                                                makeModelManualMode[rowIndex] ? (
                                                                    // Manual text input mode
                                                                    <input
                                                                        ref={inputRef}
                                                                        type="text"
                                                                        value={editValue}
                                                                        onChange={(e) => setEditValue(e.target.value)}
                                                                        onBlur={() => {
                                                                            commitEdit(rowIndex, 'make_model_manual', editValue);
                                                                            closeEditingCell();
                                                                        }}
                                                                        className="w-full px-2 py-1.5 text-sm bg-gray-800 text-gray-200 border-0 outline-none"
                                                                        style={{minWidth: '100px'}}
                                                                        placeholder="e.g. TOYOTA CAMRY"
                                                                    />
                                                                ) : (
                                                                    // Dropdown mode
                                                                    <div
                                                                        className="w-full"
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Escape') {
                                                                                e.stopPropagation();
                                                                                closeEditingCell();
                                                                                setEditValue('');
                                                                            }
                                                                        }}
                                                                    >
                                                                        <CellSelect
                                                                            items={getDropdownOptions(key)}
                                                                            value={row[key]}
                                                                            onChange={(item) => {
                                                                                const newValue = item?.value || '';
                                                                                if (item) {
                                                                                    setMakeModelDisplayMap(prev => ({
                                                                                        ...prev,
                                                                                        [newValue]: item.label
                                                                                    }));
                                                                                    // Auto-fill category from the selected model's type
                                                                                    const result = makeModelSearchResults.find(r =>
                                                                                        `${r.make.code} - ${r.model.code}` === newValue
                                                                                    );
                                                                                    if (result?.model.type) {
                                                                                        commitEdit(rowIndex, 'category', result.model.type);
                                                                                    }
                                                                                }
                                                                                commitEdit(rowIndex, key, newValue);
                                                                                closeEditingCell();
                                                                            }}
                                                                            getOptionValue={(opt) => opt.value}
                                                                            getOptionLabel={(opt) => opt.label}
                                                                            placeholder="Search make or model..."
                                                                            autoFocus
                                                                            defaultMenuIsOpen
                                                                            onBlur={closeEditingCell}
                                                                            initialInputValue={editValue}
                                                                            onInputChange={handleMakeModelSearch}
                                                                            onFocus={loadInitialMakeModelData}
                                                                            isLoading={isMakeModelSearching}
                                                                            disableClientFilter
                                                                        />
                                                                    </div>
                                                                )
                                                            ) : (
                                                                <div
                                                                    className={`
                                                                        w-full px-2 py-1.5 text-sm text-gray-200 min-h-[32px] truncate
                                                                        ${isSelected ? 'bg-blue-900/20' : ''}
                                                                        ${!isSelected && !isInSelection ? 'hover:bg-gray-800/50' : ''}
                                                                    `}
                                                                    style={{minWidth: '100px'}}
                                                                >
                                                                    {makeModelManualMode[rowIndex]
                                                                        ? row.make_model_manual
                                                                        : getDisplayLabel(key, row[key])}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Toggle button for manual/dropdown mode */}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setMakeModelManualMode(prev => ({
                                                                    ...prev,
                                                                    [rowIndex]: !prev[rowIndex]
                                                                }));
                                                                // Clear the other field when switching modes
                                                                if (makeModelManualMode[rowIndex]) {
                                                                    // Switching to dropdown mode, clear manual value
                                                                    commitEdit(rowIndex, 'make_model_manual', '');
                                                                } else {
                                                                    // Switching to manual mode, clear dropdown value
                                                                    commitEdit(rowIndex, 'make_model', '');
                                                                }
                                                            }}
                                                            className={`flex-shrink-0 p-1 mr-1 rounded transition-colors ${
                                                                makeModelManualMode[rowIndex]
                                                                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                                                    : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                                                            }`}
                                                            title={makeModelManualMode[rowIndex] ? "Switch to dropdown" : "Switch to manual entry"}
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ) : isEditing ? (
                                                    isDropdown ? (
                                                        <div
                                                            className="w-full"
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Escape') {
                                                                    e.stopPropagation();
                                                                    closeEditingCell();
                                                                    setEditValue('');
                                                                }
                                                            }}
                                                        >
                                                            <CellSelect
                                                                items={getDropdownOptions(key)}
                                                                value={row[key]}
                                                                onChange={(item) => {
                                                                    const newValue = item?.value || '';
                                                                    commitEdit(rowIndex, key, newValue);
                                                                    closeEditingCell();
                                                                }}
                                                                getOptionValue={(opt) => opt.value}
                                                                getOptionLabel={(opt) => opt.label}
                                                                placeholder="Type to search..."
                                                                autoFocus
                                                                defaultMenuIsOpen
                                                                onBlur={closeEditingCell}
                                                                initialInputValue={editValue}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="relative w-full" style={{minWidth: colHeader?.width}}>
                                                            <input
                                                                ref={inputRef}
                                                                type="text"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={() => {
                                                                    commitEdit(rowIndex, key, editValue);
                                                                    closeEditingCell();
                                                                }}
                                                                className={`w-full px-2 py-1.5 text-sm bg-gray-800 text-gray-200 border-0 outline-none ${key === 'chassis_number' ? 'pr-8' : ''}`}
                                                            />
                                                            {key === 'chassis_number' && editValue.length > 0 && (
                                                                <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium ${
                                                                    editValue.length === 17 ? 'text-green-400 animate-pulse' : 'text-red-400'
                                                                }`}>
                                                                    {editValue.length}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )
                                                ) : (
                                                    <div
                                                        className={`
                                                                relative w-full px-2 py-1.5 text-sm min-h-[32px] truncate
                                                                ${conflict && conflict.decision === 'skip' && key === 'chassis_number' ? 'text-gray-500 line-through' : 'text-gray-200'}
                                                                ${isSelected ? 'bg-blue-900/20' : ''}
                                                                ${!isSelected && !isInSelection ? 'hover:bg-gray-800/50' : ''}
                                                            `}
                                                        style={{minWidth: colHeader?.width}}
                                                    >
                                                        <span className={key === 'chassis_number' && row[key] ? 'pr-6' : ''}>{isDropdown ? getDisplayLabel(key, row[key]) : row[key]}</span>
                                                        {key === 'chassis_number' && row[key].length > 0 && (
                                                            <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium ${
                                                                row[key].length === 17 ? 'text-green-400 animate-pulse' : 'text-red-400'
                                                            }`}>
                                                                {row[key].length}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {/* Delete row button / conflict badge */}
                                    <td className="border-b border-gray-700 p-0 text-center">
                                        {conflict ? (
                                            <div className="flex items-center justify-center px-1">
                                                {conflict.decision === 'pending' ? (
                                                    // Pending: show nothing extra in this cell (panel below is open)
                                                    <span className="text-amber-400 text-xs">!</span>
                                                ) : conflict.decision === 'keep_both' ? (
                                                    <div className="flex items-center gap-1">
                                                        <span className="bg-blue-900/60 text-blue-400 text-xs px-2 py-0.5 rounded whitespace-nowrap">Keep Both</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConflictMap(prev => ({
                                                                ...prev,
                                                                [rowIndex]: { ...prev[rowIndex], decision: 'pending', isPanelOpen: true }
                                                            }))}
                                                            className="text-gray-500 hover:text-gray-300 text-xs underline ml-1"
                                                        >
                                                            Undo
                                                        </button>
                                                    </div>
                                                ) : conflict.decision === 'override' ? (
                                                    <div className="flex items-center gap-1">
                                                        <span className="bg-green-900/60 text-green-400 text-xs px-2 py-0.5 rounded whitespace-nowrap">Will Override</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConflictMap(prev => ({
                                                                ...prev,
                                                                [rowIndex]: { ...prev[rowIndex], decision: 'pending', isPanelOpen: true }
                                                            }))}
                                                            className="text-gray-500 hover:text-gray-300 text-xs underline ml-1"
                                                        >
                                                            Undo
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1">
                                                        <span className="bg-gray-800 text-gray-500 text-xs px-2 py-0.5 rounded whitespace-nowrap">Skipped</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConflictMap(prev => ({
                                                                ...prev,
                                                                [rowIndex]: { ...prev[rowIndex], decision: 'pending', isPanelOpen: true }
                                                            }))}
                                                            className="text-gray-500 hover:text-gray-300 text-xs underline ml-1"
                                                        >
                                                            Undo
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => deleteRow(rowIndex)}
                                                            className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-900/20 rounded transition-colors"
                                                            title="Delete row"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => deleteRow(rowIndex)}
                                                className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-900/20 rounded transition-colors"
                                                title="Delete row (Ctrl+Delete for selected rows)"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {/* Conflict sub-row panel */}
                                {conflict && conflict.isPanelOpen && (
                                    <tr>
                                        <td
                                            colSpan={COLUMN_HEADERS.length + 2}
                                            className="p-0 border-b border-amber-500/40 bg-gray-800"
                                        >
                                            <div className="px-4 py-3">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    <span className="text-amber-400 text-sm font-medium">Chassis already exists in the system</span>
                                                </div>
                                                <div className="mb-3">
                                                    <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Existing Car:</p>
                                                    <div className="flex items-center gap-6 flex-wrap">
                                                        <div>
                                                            <span className="text-gray-400 text-xs">Voyage: </span>
                                                            <span className="text-gray-200 text-sm">{conflict.existingCar.voyageCode || '—'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400 text-xs">Make/Model: </span>
                                                            <span className="text-gray-200 text-sm">
                                                                {[conflict.existingCar.makeName, conflict.existingCar.modelName].filter(Boolean).join(' ') || '—'}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400 text-xs">Shipper: </span>
                                                            <span className="text-gray-200 text-sm">{conflict.existingCar.shipperName || '—'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400 text-xs">Consignee: </span>
                                                            <span className="text-gray-200 text-sm">{conflict.existingCar.consigneeName || '—'}</span>
                                                        </div>
                                                        {conflict.existingCar.year && (
                                                            <div>
                                                                <span className="text-gray-400 text-xs">Year: </span>
                                                                <span className="text-gray-200 text-sm">{conflict.existingCar.year}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setConflictMap(prev => ({
                                                            ...prev,
                                                            [rowIndex]: { ...prev[rowIndex], decision: 'keep_both', isPanelOpen: false }
                                                        }))}
                                                        className="bg-blue-700 hover:bg-blue-600 text-white text-xs px-3 py-1.5 rounded"
                                                    >
                                                        Keep Both
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConflictMap(prev => ({
                                                            ...prev,
                                                            [rowIndex]: { ...prev[rowIndex], decision: 'override', isPanelOpen: false }
                                                        }))}
                                                        className="bg-green-700 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded"
                                                    >
                                                        Override
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConflictMap(prev => ({
                                                            ...prev,
                                                            [rowIndex]: { ...prev[rowIndex], decision: 'skip', isPanelOpen: false }
                                                        }))}
                                                        className="bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs px-3 py-1.5 rounded"
                                                    >
                                                        Remove from Batch
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* Status bar */}
                <div className="mt-2 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>
                        {validRowCount} valid rows ready | {rows.length} total rows
                        {selectionCount > 1 && ` | ${selectionCount} cells selected`}
                        {selectedCell && selectionCount <= 1 && ` | Row ${selectedCell.row + 1}, Col ${selectedCell.col + 1}`}
                    </span>
                </div>
            </div>

            {/* Failed Cars Modal */}
            {showFailedModal && failedCars.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    Creation Result
                                </h2>
                                <button
                                    onClick={() => setShowFailedModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                            {lastResult && (
                                <div className="mt-2 flex gap-4">
                                    <span className="text-green-600 dark:text-green-400">
                                        {lastResult.created} created successfully
                                    </span>
                                    {lastResult.updated > 0 && (
                                        <span className="text-blue-600 dark:text-blue-400">
                                            {lastResult.updated} overridden
                                        </span>
                                    )}
                                    <span className="text-red-600 dark:text-red-400">
                                        {lastResult.failed} failed
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                Failed Entries:
                            </h3>
                            <div className="space-y-2">
                                {failedCars.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                                    >
                                        <div>
                                            <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                                                Row {item.row}:
                                            </span>
                                            <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                                {item.chassis || '(empty)'}
                                            </span>
                                        </div>
                                        <span className="text-sm text-red-600 dark:text-red-400">
                                            {item.reason}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                  const text = failedCars.map((item) => item.chassis).join('\n');
                                  navigator.clipboard.writeText(text);
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                Copy Failed
                            </button>
                            <button
                                onClick={() => setShowFailedModal(false)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={hideToast}
                />
            )}
        </Layout>
    );
};

export default BulkAddCarsPage;
